import Alert from "@amzn/meridian/alert";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Card, { CardHeader } from "@amzn/meridian/card";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Input from "@amzn/meridian/input";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import React, { useEffect, useState } from "react";
import { getStationCode } from "../../network/NetworkUtil";
import AppLoader from "../common/appLoader";
import { filterBosonVariance, filterDefaultShortVariance, getIssueLinkRegExp, getReadableLabel, getRemittanceRequestParams, groupBy, insertVarianceByUniqueReasonCode } from "../common/commonUtils";
import {RemittanceStatus, ROUND_OFF_SHORT, VarianceType} from "../common/remittanceV2Constants";
import Currency from "../common/currency";
import { getRemittanceList, resetRemittanceRequest, verifyRemittanceV2Request } from "../remittance/network/RemittanceAPI";
import AlertModal from "./alertModal";
import SectionDetails from "./deposit/sectionDetails";
import SubmitRemittanceV2Modal from "./submitRemittanceV2Modal";
import VarianceCard from "../common/varianceCard";
import { STATION_SHORT_VARIANCE_REASON } from "../common/remittanceV2Constants";
import VarianceAmount from "../common/varianceAmount";
import {CurrencySymbol, StringConstants} from "../common/constants";
import Tag from "@amzn/meridian/tag";

const ttLinkRegExp = getIssueLinkRegExp();

const VerifyRemittanceV2Modal = (props) => {
    const [alertModal, setAlertModal] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [inputError, setInputError] = useState();
    const [hasShortVariance, setHasShortVariance] = useState();
    const [selectedVarianceReason, setSelectedVarianceReason] = useState({});
    const [stationShortTTLink, setStationShortTTLink] = useState();
    const [stationShortVariance, setStationShortVariance] = useState();
    const [actualAmount, setActualAmount] = useState({unit: props.currencyUnit});
    const [submittedAmount, setSubmittedAmount] = useState({unit: props.currencyUnit});
    const [isVerified, setIsVerified] = useState();
    const [uniqueBosonVariances, setUniqueBosonVariances] = useState([]);
    const [uniqueExternalVendorVariances, setUniqueExternalVendorVarance] = useState([]);
    const [transactionId, setTransactionId] = useState();

    const stationShortVarianceReasons = STATION_SHORT_VARIANCE_REASON;

    const onVarianceReasonChange = (reasonCode) => {
        setSelectedVarianceReason(stationShortVarianceReasons.filter((varianceReason) => varianceReason.reasonCode === reasonCode)[0]);
        setStationShortTTLink();
    };

    /**
     * Method to process the remittance list from the response to set the state of modal
     * @param {List} remittanceList 
     */
    const processRemittanceList = (remittanceList) => {
        let actualAmountValue = 0, totalVarianceValue = 0, uniqueBosonVariances = [], uniqueExternalVendorVariances = [];
        const variancesByUniqueReasonCodeMap = {}, variancesByUniqueReasonCodeList = [];
        //filtering remittance with status PENDING_SUBMISSION
        const filteredRemittanceList = remittanceList.filter((remittance) => remittance.status === RemittanceStatus.PENDING_SUBMISSION);

        if (filteredRemittanceList.length === 0) {
            setAlertModal({show: true, title:"Info", message: "No Remittance exists to verify", onClose: reload});
            return;
        }

        const stationVariance = filteredRemittanceList[0].stationVariance;
        //Getting transaction Id from stationVariance.
        if (stationVariance) {
            setTransactionId(stationVariance.transactionId);
            let isVerified = true;
            //Checking if remittance is verified 
            filteredRemittanceList.forEach(remittance => {
                isVerified = isVerified && remittance.stationVariance.isVerified;
            });
            if (isVerified) {
                onRemittanceVerified();
                return;
            }
            setShowVerifyModal(true);
        }

        filteredRemittanceList.forEach((remittance) => {
            //summing up all remittance amount
            actualAmountValue += remittance.actualAmount.value;
            //summing up variance together having the same variance reason
            remittance.stationVariance.stationVarianceList.forEach(variance => { insertVarianceByUniqueReasonCode(variancesByUniqueReasonCodeMap, variance) });            
        });

        //map to array conversion i.e. {key1: data1, key2: data2} to [data1, data2]
        Object.entries(variancesByUniqueReasonCodeMap).forEach(([, data]) => { variancesByUniqueReasonCodeList.push(data) });

        //filtering boson and external vendor variances
        uniqueBosonVariances = variancesByUniqueReasonCodeList.filter(filterBosonVariance);
        uniqueExternalVendorVariances = variancesByUniqueReasonCodeList.filter(variance => !filterBosonVariance(variance));

        //summing up all variances amount to get total variance
        totalVarianceValue = variancesByUniqueReasonCodeList.reduce(
            (totalVarianceValue, variance) => {
                if (variance.type === VarianceType.SHORT) {
                    totalVarianceValue -= variance.amount.value;
                } else {
                    totalVarianceValue += variance.amount.value;
                }
                return totalVarianceValue;
            },
            totalVarianceValue
        );

        //getting DEFAULT_STATION_SHORT variance
        const defaultStationShortVariance = uniqueBosonVariances.filter(filterDefaultShortVariance)[0];

        uniqueBosonVariances = uniqueBosonVariances.filter(variance => variance !== defaultStationShortVariance)

        //if DEFAULT_STATION_VARIANCE exists then show variance drop down to specify reason for the short variance
        if (defaultStationShortVariance) {
            setStationShortVariance(defaultStationShortVariance);
            setHasShortVariance(true);
        }

        //Grouping variance list to show as a grid with 3 column
        setUniqueBosonVariances(groupBy(uniqueBosonVariances, 3));
        setUniqueExternalVendorVarance(groupBy(uniqueExternalVendorVariances, 3));

        setActualAmount({...actualAmount, value: actualAmountValue});
        setSubmittedAmount({...submittedAmount, value: actualAmountValue + totalVarianceValue});
    };

    useEffect(() => {
        getRemittanceList(getRemittanceRequestParams())
            .then((response) => {
                if (response.status === 200) {
                    const { data: { remittanceList } } = response;
                    processRemittanceList(remittanceList);
                } else {
                    setAlertModal({
                        show: true,
                        type: "error",
                        title: "Error",
                        message: "Failed to fetch bank deposits",
                        onClose: reload
                    });
                }
                setIsLoading(false);
            })
            .catch(() => {
                setAlertModal({
                    show: true,
                    type: "error",
                    title: "Error",
                    message: "Unexpected error occurred",
                    onClose: reload
                });
                setIsLoading(false);
            });
    }, []);

    const reload = () => {
        window.location.reload();
    };

    const clearInputErrorMessage = () => {
        setInputError();
    };

    /**
     * Validate reason and tt link for default short station variance
     * @param {boolean} verified 
     * @returns boolean
     */
    const isValidInput = verified => {
        if (verified && hasShortVariance) {
            if (!selectedVarianceReason || !selectedVarianceReason.reasonCode) {
                setInputError("Please select a reason");
                return false;
            } else if (
                selectedVarianceReason.requireIssueLink &&
                !ttLinkRegExp.test(stationShortTTLink)
            ) {
                setInputError("Please enter a valid issue link");
                return false;
            } else if (selectedVarianceReason.reasonCode === ROUND_OFF_SHORT
                        && Math.abs(stationShortVariance.amount.value) >= 1) {
                //Validate the amount for reason : ROUND_OFF_SHORT which should be less than Rs1.
                setInputError("Amount is greater than " + CurrencySymbol[props.currencyUnit] + " 1");
                return false;
            }
        }
        return true;
    };

    const onRemittanceVerified = () => {
        // Show the SubmitRemittanceModal
        setIsVerified(true);
        // Hide the VerifyRemittanceModal
        setShowVerifyModal(false);
    };

    /**
     * Sets the AlertModal's content
     * @param {boolean} verified 
     */
    const setAlertModalAfterVerify = (verified) => {
        let title = "Verified", message = "Successfully verified the remittance";
        let onClose = onRemittanceVerified;
        if (!verified) {
            title = "Rejected";
            message = "Successfully rejected the remittance";
            onClose = reload;
        }
        setAlertModal({
            show: true,
            type: "success",
            title: title,
            message: message,
            onClose: onClose,
        });
    }

    /**
     * Method to verify/reject the remittance
     * @param {boolean} verified 
     */
    const verifyRemittance = (verified) => {
        if (isValidInput(verified)) {
            setIsLoading(true);
            setErrorMessage(undefined);
            verifyRemittanceV2Request(createRequestData(verified))
                .then((response) => {
                    if (response.status === 200) {
                        const { data } = response;
                        if (data.responseCode === StringConstants.SUCCESS) {
                            setAlertModalAfterVerify(verified);
                        } else {
                            setErrorMessage("Error: " + getReadableLabel(data.errorCode));
                        }
                    } else {
                        setErrorMessage("Unable to process the remittance");
                    }
                    setIsLoading(false);
                })
                .catch(() => {
                    setErrorMessage("Unexpected error occurred");
                    setIsLoading(false);
                });
        }
    };

    /**
     * Create request object for VerifyRemittanceRequest
     * @param {boolean} verified 
     * @returns request object
     */
    const createRequestData = (verified) => {
        const requestData = {
            stationCode: getStationCode(),
            verified: verified,
            reason: selectedVarianceReason.reasonCode,
            transactionId: transactionId
        };
        if (stationShortTTLink) {
            requestData.reason = requestData.reason + StringConstants.SEPARATOR + stationShortTTLink;
        }

        return requestData;
    };

    const resetRemittance = () => {
        setIsLoading(true);
        resetRemittanceRequest({
            stationCode: getStationCode()
        }).then(response => {
            if (response.status === 200) {
                const { data } = response;
                if (data.responseCode === StringConstants.SUCCESS) {
                    setAlertModal({
                        show: true,
                        title: "Success",
                        type: "success",
                        message: "Remittance has been reset successfully",
                        onClose: reload
                    });
                } else {
                    setAlertModal({
                        show: true,
                        type: "error",
                        title: "Error",
                        message: getReadableLabel(data.errorCode),
                        onClose: reload
                    });
                }
            } else {
                setErrorMessage("Unable to reset the remittance")
            }
            setIsLoading(false);
        }).catch(() => {
            setAlertModal({
                show: true, 
                title:"Error",
                type: "error", 
                message: "Unexpected error occurred", 
                onClose: reload
            });
            setIsLoading(false);
        });
    }

    return (
        <React.Fragment>
            {isLoading && <AppLoader />}
            <Modal
                title="Verify Remittance"
                open={showVerifyModal}
                scrollContainer="viewport"
                describedById="modal-description"
            >
                <Column spacing="medium">
                    <Column spacing="small">
                        {transactionId ? <Tag>Transaction ID: {transactionId}</Tag> : <Tag type="warning">Transaction Id is not generated</Tag>}
                        <SectionDetails
                            title="Overview"
                            description="Amount that should ideally be submitted and actual amount getting submitted."
                        />
                        <Row
                            spacing="large"
                            alignmentHorizontal="center"
                            widths={270}
                        >
                            <Card>
                                <CardHeader>
                                    <Heading>Actual Amount</Heading>
                                </CardHeader>
                                <Currency amount={actualAmount} />
                            </Card>
                            <Card>
                                <CardHeader>
                                    <Heading>Submitted Amount</Heading>
                                </CardHeader>
                                <Currency amount={submittedAmount} />
                            </Card>
                        </Row>
                    </Column>
                    <Column spacing="small">
                        <SectionDetails
                            title="Station Variance"
                            description="Station variance breakup"
                        />
                        <Column>
                            {uniqueBosonVariances.length ? (
                                uniqueBosonVariances.map(
                                    (varianceGroup) => (
                                        <Row>
                                            {varianceGroup.map((variance) => (
                                                <VarianceCard
                                                    width={180}
                                                    variance={variance}
                                                />
                                            ))}
                                        </Row>
                                    )
                                )
                            ) : (
                                <Alert>
                                    There is no excess variance declared by the station
                                </Alert>
                            )}
                        </Column>
                    </Column>
                    <Column spacing="small">
                        <SectionDetails
                            title="CCA Variance"
                            description="Variance breakup by CCA"
                        />
                        <Column>
                            {uniqueExternalVendorVariances.length > 0 ? (
                                uniqueExternalVendorVariances.map((varianceGroup) => (
                                    <Row>
                                        {varianceGroup.map((variance) => (
                                            <VarianceCard
                                                width={180}
                                                variance={variance}
                                            />
                                        ))}
                                    </Row>
                                ))
                            ) : (
                                <Alert>There is no variance added by CCA</Alert>
                            )}
                        </Column>
                    </Column>
                    {hasShortVariance && (
                        <Column spacing="small">
                            <Row widths={540}>
                                <SectionDetails
                                    title="Station short variance"
                                    description="Please specify the reason and enter issue link for the station short variance. Please specify the reason for the cash not submitted to CCA"
                                />
                            </Row>
                            <Row>
                                <Box
                                    width={180}
                                    type="outline"
                                    spacingInset="small"
                                >
                                    <VarianceAmount
                                        amount={stationShortVariance.amount}
                                        type={VarianceType.SHORT}
                                    />
                                </Box>
                                <Select
                                    value={selectedVarianceReason.reasonCode}
                                    onChange={onVarianceReasonChange}
                                    onFocus={clearInputErrorMessage}
                                    placeholder="Select a reason"
                                    width={180}
                                >
                                    {stationShortVarianceReasons.map(
                                        (varianceReason) => (
                                            <SelectOption
                                                value={varianceReason.reasonCode}
                                                label={varianceReason.reasonLabel}
                                                key={varianceReason.reasonCode}
                                            />
                                        )
                                    )}
                                </Select>
                                <Input
                                    width={180}
                                    placeholder="Enter issue link"
                                    value={stationShortTTLink}
                                    disabled={
                                        !selectedVarianceReason.requireIssueLink
                                    }
                                    onChange={setStationShortTTLink}
                                    onFocus={clearInputErrorMessage}
                                />
                            </Row>
                            {inputError && (
                                <Alert type="error">{inputError}</Alert>
                            )}
                        </Column>
                    )}
                    {errorMessage && <Alert type="error">{errorMessage}</Alert>}
                </Column>
                <ModalFooter>
                    <Row widths={["grid-2", "fill"]} >
                        <Column>
                           <Row alignmentHorizontal="left">
                            <Button
                                type="tertiary"
                                size="large"
                                disabled={isLoading}
                                onClick={reload}
                            >
                                Close
                            </Button>
                           </Row>
                        </Column>
                        <Column>
                            {transactionId ? 
                                <Row alignmentHorizontal="right">
                                    <Button
                                        type="secondary"
                                        size="large"
                                        disabled={isLoading}
                                        onClick={() => verifyRemittance(false)}
                                    >
                                        Reject
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        disabled={isLoading}
                                        onClick={() => verifyRemittance(true)}
                                    >
                                        Verify
                                    </Button>
                                </Row> 
                                :
                                <Row alignmentHorizontal="right">
                                    <Button
                                        type="secondary"
                                        size="large"
                                        disabled={isLoading}
                                        onClick={resetRemittance}
                                    >
                                        Reset
                                    </Button>
                                </Row>
                            }
                        </Column>
                    </Row>
                </ModalFooter>
            </Modal>
            {!isVerified && <AlertModal {...alertModal} />}
            {isVerified && transactionId && (
                <SubmitRemittanceV2Modal
                    currencyUnit={props.currencyUnit}
                />
            )}
        </React.Fragment>
    );
};

export default VerifyRemittanceV2Modal;



// WEBPACK FOOTER //
// ./src/component/modal/verifyRemittanceV2Modal.js